import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Select from '@8x8/oxygen-select'

import { getModerationSettings, getUser } from '~/selectors/room-settings'
import { getContacts, updateActiveParticipantRole, updateModerationSettings } from '~/actions/room-settings'
import { translate } from '~/core/translate'
import { AddParticipants, SettingsContainer, SettingsDescription, SettingsTitle, StyledLegend } from './components'
import { MODERATION_TYPE, ROLE } from '~/core/constants'
import ParticipantsView from './participant-view'

const ActiveParticipantsSettings = () => {
  const dispatch = useDispatch()
  const [fetching, setFetching] = useState(false)
  const [filterValue, setFilterValue] = useState('')
  const [items, setItems] = useState([])
  const user = useSelector(getUser)
  const { activeParticipantUsers } = useSelector(getModerationSettings)

  const handleSelectChange = useCallback(value => {
    setFilterValue(value)
  }, [])

  const fetchContacts = useCallback(async query => {
    setFetching(true)
    try {
      const response = await dispatch(getContacts({ query }))
      const formatted = response
        .map(contact => ({
          value: contact.user_id,
          label: contact.name
        }))
        .filter(({ value }) => !!value && value !== user.id)

      setItems(formatted)
    } finally {
      setFetching(false)
    }
  }, [dispatch])

  useEffect(() => {
    fetchContacts(filterValue)
  }, [filterValue, fetchContacts])

  const handleActiveUsersUpdate = useCallback(user => {
    if (!user) {
      return
    }

    const { value, label } = user
    if (activeParticipantUsers?.find(({ userId }) => userId === value)) {
      return
    }

    dispatch(updateModerationSettings({
      moderationType: MODERATION_TYPE.SPECIFIC_MEMBERS,
      activeParticipantUsers: [
        ...(activeParticipantUsers || []),
        { userId: value, name: label, activeParticipantType: ROLE.PARTICIPANT }
      ]
    }))
  }, [dispatch, activeParticipantUsers])

  const handleRoleUpdate = useCallback((userId, role) => {
    dispatch(updateActiveParticipantRole({ userId, role }))
  }, [])

  const handleRemoveActiveUser = useCallback(userId => {
    dispatch(updateModerationSettings({
      moderationType: MODERATION_TYPE.SPECIFIC_MEMBERS,
      activeParticipantUsers: activeParticipantUsers?.filter(({ userId: id }) => id !== userId)
    }))
  }, [dispatch, activeParticipantUsers])

  return (
    <SettingsContainer>
      <SettingsTitle>{translate('active_participants')}</SettingsTitle>
      <SettingsDescription>{translate('active_participants_description')}</SettingsDescription>
      <AddParticipants>
        <StyledLegend>{translate('add_active_participants')}</StyledLegend>
        <Select
          placeholder={translate('search_for_contacts')}
          options={items}
          onInputChange={handleSelectChange}
          value={filterValue}
          components={{
            DropdownIndicator: undefined
          }}
          isSearchable
          menuIsOpen={Boolean(filterValue)}
          onChange={handleActiveUsersUpdate}
          noOptionsMessage={() => translate('no_results_found')}
          isLoading={fetching}
          testId='participants-selection'
        />
        <SettingsDescription>{translate('active_participants_role')}</SettingsDescription>
        {activeParticipantUsers?.length > 0 && (
          <ParticipantsView
            defaultModerator={user}
            onRoleUpdate={handleRoleUpdate}
            onRemove={handleRemoveActiveUser}
            participants={activeParticipantUsers}
          />
        )}
      </AddParticipants>
    </SettingsContainer>
  )
}

export default ActiveParticipantsSettings
